import React from "react";

import { closestCenter, DndContext, DragOverlay } from "@dnd-kit/core";
import {
  rectSortingStrategy,
  SortableContext,
  useSortable,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { GripVertical, Play } from "lucide-react";

import {
  renumberSortOrder,
  SORTABLE_DROP_ANIMATION,
  SORTABLE_TRANSITION,
  useSortableListState,
  useSortableSensors,
} from "./sortableDnd";
import { getVideoRoleKey, VIDEO_ROLE_ACCORDIONS } from "./videoOrderUtils";
import TruncatedTooltip from "../../Components/TruncatedTooltip";
import { LibraryVideo } from "../../Store/Reducers/VideoSlice";
import { getYouTubeThumbnailUrl } from "../../Utils/Helper/videoUrl";

const getLibraryId = (video: LibraryVideo) => video.id;

const getRoleLabels = (roles: string[]): string[] => {
  const roleKeys = new Set(roles.map(role => getVideoRoleKey(role)));
  return VIDEO_ROLE_ACCORDIONS.filter(role => roleKeys.has(role.value)).map(
    role => role.label
  );
};

interface SortableLibraryGridProps {
  videos: LibraryVideo[];
  onVideosChange: (videos: LibraryVideo[]) => void;
  onPreview: (video: LibraryVideo) => void;
  /** Id of the card that should pulse after a move. */
  highlightId?: string | null;
  onMoved?: (id: string) => void;
  gridClassName?: string;
}

const LibraryOrderCard: React.FC<{
  video: LibraryVideo;
  displayOrder: number;
  onPreview: (video: LibraryVideo) => void;
}> = ({ video, displayOrder, onPreview }) => {
  const thumbnailUrl = getYouTubeThumbnailUrl(video.video_url);
  const roleLabels = getRoleLabels(video.assigned_roles);

  return (
    <article className="library-video-card library-video-card--sortable">
      <button
        type="button"
        className="library-video-card__thumb"
        onClick={() => onPreview(video)}
        title="Preview video"
      >
        <span className="library-video-card__order-badge">{displayOrder}</span>
        {thumbnailUrl ? (
          <img src={thumbnailUrl} alt={video.title || "Video"} />
        ) : (
          <Play size={28} className="text-gray-300" />
        )}
      </button>
      <div className="library-video-card__body">
        <TruncatedTooltip
          tooltip={video.title || "Untitled Video"}
          className="library-video-card__title"
        >
          {video.title || "Untitled Video"}
        </TruncatedTooltip>
        {video.description && (
          <TruncatedTooltip
            tooltip={video.description}
            maxLines={2}
            className="library-video-card__description"
          >
            {video.description}
          </TruncatedTooltip>
        )}
      </div>
      <div className="library-video-card__footer">
        {roleLabels.length > 0 ? (
          <TruncatedTooltip
            tooltip={`Assigned to: ${roleLabels.join(", ")}`}
            className="library-video-card__roles"
          >
            Assigned to {roleLabels.length}{" "}
            {roleLabels.length === 1 ? "role" : "roles"}
          </TruncatedTooltip>
        ) : (
          <span className="library-video-card__roles library-video-card__roles--none">
            Not assigned yet
          </span>
        )}
      </div>
    </article>
  );
};

const SortableLibraryItem: React.FC<{
  video: LibraryVideo;
  displayOrder: number;
  highlighted: boolean;
  onPreview: (video: LibraryVideo) => void;
}> = ({ video, displayOrder, highlighted, onPreview }) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    setActivatorNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({
    id: video.id,
    transition: SORTABLE_TRANSITION,
  });

  const setRefs = (node: HTMLDivElement | null) => {
    setNodeRef(node);
    setActivatorNodeRef(node);
  };

  return (
    <div
      ref={setRefs}
      style={{ transform: CSS.Transform.toString(transform), transition }}
      className={`sortable-video${isDragging ? " is-placeholder is-drag-active" : ""}${
        highlighted ? " is-reorder-highlight" : ""
      }`}
      {...attributes}
      {...listeners}
      aria-label={`${video.title || "Video"} — drag to reorder`}
    >
      <span className="sortable-video__handle" aria-hidden="true">
        <GripVertical size={14} />
        Drag
      </span>
      <LibraryOrderCard
        video={video}
        displayOrder={displayOrder}
        onPreview={onPreview}
      />
    </div>
  );
};

/**
 * Drag-and-drop grid for default Video Library catalog order within one topic.
 */
const SortableLibraryGrid: React.FC<SortableLibraryGridProps> = ({
  videos,
  onVideosChange,
  onPreview,
  highlightId = null,
  onMoved,
  gridClassName = "admin-video-container__grid library-video-grid",
}) => {
  const sensors = useSortableSensors();
  const {
    activeId,
    ids,
    reset,
    handleDragStart,
    handleDragOver,
    handleDragEnd,
    previewOrders: buildPreviewOrders,
  } = useSortableListState({
    items: videos,
    getId: getLibraryId,
    onItemsChange: onVideosChange,
    onMoved,
    transformMoved: renumberSortOrder,
  });

  const previewOrders = buildPreviewOrders(
    video => video.sort_order,
    renumberSortOrder
  );

  const orderOf = (video: LibraryVideo) =>
    previewOrders.get(video.id) ?? video.sort_order ?? 0;

  const activeVideo = activeId
    ? videos.find(video => video.id === activeId)
    : undefined;

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={closestCenter}
      onDragStart={handleDragStart}
      onDragOver={handleDragOver}
      onDragEnd={handleDragEnd}
      onDragCancel={reset}
    >
      <SortableContext items={ids} strategy={rectSortingStrategy}>
        <div
          className={`${gridClassName} sortable-video-grid${
            activeId ? " is-sorting" : ""
          }`}
        >
          {videos.map(video => (
            <SortableLibraryItem
              key={video.id}
              video={video}
              displayOrder={orderOf(video)}
              highlighted={highlightId === video.id}
              onPreview={onPreview}
            />
          ))}
        </div>
      </SortableContext>
      <DragOverlay dropAnimation={SORTABLE_DROP_ANIMATION}>
        {activeVideo ? (
          <div className="sortable-video sortable-video--overlay">
            <span className="sortable-video__handle" aria-hidden="true">
              <GripVertical size={14} />
              Drag
            </span>
            <LibraryOrderCard
              video={activeVideo}
              displayOrder={orderOf(activeVideo)}
              onPreview={() => undefined}
            />
          </div>
        ) : null}
      </DragOverlay>
    </DndContext>
  );
};

export default SortableLibraryGrid;
